import React from 'react';
import { getSensitiveUrlParam } from '#modules/utils/sensitiveUrlParams';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { FormattedMessage, useIntl } from 'react-intl';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';
import {
  saveAuthToken,
  isLiquidNobleLoggedIn,
  getSessionUnlockingKey,
} from '#modules/utils/jwt';
import { useLogout } from '#modules/api/User';
import { DialogBody } from '#components/Elements/Dialog/Dialog';
import Spinner from '#components/Elements/Spinner/Spinner';
import DefaultLayout from '#components/Layouts/DefaultLayout/DefaultLayout';
import { Field as FormField, Form } from 'react-final-form';
import { useRedirect } from '#modules/hooks/navigation';
import { errorResponseNotify } from '#modules/utils/errors';
import ChevronLeftIcon from '#components/Elements/SVGIcon/ChevronLeftIcon';
import Label from '#components/Elements/Form/Field/Label';
import Input from '#components/Elements/Form/Controls/Input';
import Button from '#components/Elements/Button/Button';
import { trackEvent, trackSignUp } from '#modules/utils/analytics';
import {
  useValidators,
  fieldIsRequired,
  QUIET_VALIDATION_FAILURE,
} from '#modules/utils/validatorsLocalized';

import styles from './LogIn.scss';

export default function EmailVerificationPage() {
  const intl = useIntl();
  const liquidNobleApi = useLiquidNobleApi();
  const redirect = useRedirect();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const isLoggedIn = isLiquidNobleLoggedIn();
  const onLogout = useLogout();

  const onBack = React.useCallback(() => {
    onLogout();
  }, [onLogout]);

  const searchParamsVerificationCode = getSensitiveUrlParam('code') ?? '';
  const [autoSubmit, setAutoSubmit] = React.useState(
    searchParamsVerificationCode.length > 0,
  );
  const initialValues = React.useMemo(
    () => ({
      verificationCode: searchParamsVerificationCode,
    }),
    [searchParamsVerificationCode],
  );

  const validateRequired = useValidators(fieldIsRequired);

  const onResendEmail = React.useCallback(async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await liquidNobleApi.post('/user/email/verify/resend/');
      toast.success(
        intl.formatMessage({
          defaultMessage: 'Verification email resent',
        }),
      );
    } catch (error) {
      errorResponseNotify(error);
      console.log("'/user/email/verify/resend/' error: ", error.response);
    }

    setTimeout(() => {
      setSubmitting(false);
    }, 2000);
  }, [liquidNobleApi, submitting, setSubmitting, intl]);

  const onVerifyCode = React.useCallback(
    async (code, form) => {
      const unlocking_key = getSessionUnlockingKey();
      const postData = {
        code: code.replace(/\s/g, ''),
        unlocking_key: unlocking_key || '',
      };
      try {
        const response = await liquidNobleApi.post(
          '/user/email/verify/',
          postData,
        );
        const { data } = response;

        form?.reset();
        if (data?.token) {
          saveAuthToken(data.token);
        }
        if (data?.verified) {
          trackSignUp();
          trackEvent('Lead', {
            status: true,
            content_name: 'email_verified',
          });

          const instructions =
            data.token || !!unlocking_key
              ? ''
              : ` ${intl.formatMessage({
                  defaultMessage:
                    ' You can now log in with your email and password.',
                })}`;
          toast.success(
            intl.formatMessage(
              {
                defaultMessage:
                  'Email address {email} verified successfully.{instructions}',
              },
              { email: data.verified, instructions },
            ),
          );

          if (!data.token) {
            redirect('/log-in');
          } else {
            redirect('/dashboard');
          }
        }
      } catch (error) {
        errorResponseNotify(error);
        console.error("'/user/email/verify' error: ", error);
      }
    },
    [liquidNobleApi, redirect, intl],
  );

  const onSubmit = React.useCallback(
    async (values, form) => {
      const { verificationCode } = values;
      if (submitting) {
        return;
      }
      setSubmitting(true);
      onVerifyCode(verificationCode, form);
      setTimeout(() => {
        setSubmitting(false);
      }, 2000);
    },
    [onVerifyCode, submitting],
  );

  React.useEffect(() => {
    if (loading || !autoSubmit) {
      return;
    }

    setAutoSubmit(false);
    setLoading(true);
    onSubmit(initialValues);
  }, [loading, autoSubmit, initialValues, onSubmit, setLoading]);

  React.useEffect(() => {
    if (!loading) {
      return;
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [loading, setLoading]);

  if (loading) {
    return (
      <div className={styles.loginSpinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <DefaultLayout
      showNavigation={false}
      enableServiceBanner
      allowModalBackgroundInteraction
      modalTest="login"
      forceModalContent={
        <>
          <DialogBody className={styles.loginBody}>
            <Form
              onSubmit={onSubmit}
              initialValues={initialValues}
              render={({ handleSubmit, ...formProps }) => (
                <>
                  <div
                    className={classNames(
                      styles.loginHeader,
                      styles.createAccountRow,
                    )}
                  >
                    <button
                      className={classNames(
                        styles.transparentButton,
                        styles.centeredButton,
                      )}
                      onClick={() => {
                        formProps.form.reset();
                        onBack();
                      }}
                    >
                      <ChevronLeftIcon className={styles.backChevron} />
                    </button>
                    <FormattedMessage defaultMessage="Verify Email Address" />
                  </div>
                  <div className={styles.headerSubtitle}>
                    <FormattedMessage defaultMessage="We've sent a verification code to your email address to confirm ownership. Please check your inbox and enter the code below:" />
                  </div>
                  <div>
                    <form
                      onSubmit={handleSubmit}
                      className={styles.loginForm}
                      autoComplete="off"
                    >
                      <FormField
                        name="verificationCode"
                        validate={validateRequired}
                      >
                        {({
                          input,
                          meta: {
                            touched,
                            error,
                            submitError,
                            dirtySinceLastSubmit,
                          },
                        }) => (
                          <Label>
                            <FormattedMessage defaultMessage="Verification code" />
                            <Input
                              disableAutocomplete
                              disableOnFocus
                              type="text"
                              placeholder={intl.formatMessage({
                                defaultMessage:
                                  'Enter verification code sent to your email',
                              })}
                              size="medium"
                              maxLength={32}
                              color={
                                (touched &&
                                  error &&
                                  error !== QUIET_VALIDATION_FAILURE) ||
                                (submitError && !dirtySinceLastSubmit)
                                  ? 'danger'
                                  : null
                              }
                              {...input}
                            />
                          </Label>
                        )}
                      </FormField>
                      <div className={styles.actionButtons}>
                        <Button submit color="primary" disabled={submitting}>
                          <FormattedMessage defaultMessage="Verify" />
                        </Button>
                        {isLoggedIn && (
                          <Button
                            color="primary"
                            disabled={submitting}
                            onClick={onResendEmail}
                          >
                            <FormattedMessage defaultMessage="Re-send email" />
                          </Button>
                        )}
                      </div>
                    </form>
                  </div>
                </>
              )}
            />
            <div className={styles.loginFooter} />
          </DialogBody>
        </>
      }
    />
  );
}
